import React, { useState } from 'react';
import { Check, Calendar, User, Phone, Mail } from 'lucide-react';
import { toast } from 'sonner';

const BookClass = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    discipline: '',
    preferredTime: '',
    message: ''
  });

  const [isSubmitted, setIsSubmitted] = useState(false);

  const disciplines = ['MMA', 'Kickboxing', 'BJJ', 'Combat Fitness', 'Karate', 'Self Defense'];
  const timeSlots = [
    'Morning (6:00 AM - 9:00 AM)',
    'Evening (5:00 PM - 9:00 PM)',
    'Weekend (9:00 AM - 1:00 PM)',
    'Flexible'
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    toast.info('Booking requests are temporarily disabled. Please contact us directly.');
    return;

    // Basic validation
    if (!formData.name || !formData.phone || !formData.discipline || !formData.preferredTime) {
      toast.error('Please fill in all required fields');
      return;
    }

    /*
    FastAPI + MongoDB Atlas submission is intentionally paused for now.
    try {
      const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/$/, '');
      const response = await fetch(`${API_BASE_URL}/api/book-class`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          program: formData.discipline,
          message: `${formData.preferredTime} | ${formData.message}`
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error("Failed to submit");
      }

      console.log("Success:", data);

      // Show success state
      setIsSubmitted(true);
      toast.success('Booking request submitted successfully!');

      // Reset form after 3 seconds
      setTimeout(() => {
        setIsSubmitted(false);
        setFormData({
          name: '',
          email: '',
          phone: '',
          discipline: '',
          preferredTime: '',
          message: ''
        });
      }, 3000);

    } catch (error) {
      console.error("Error:", error);
      toast.error("Something went wrong. Please try again.");
    }
    */
  };

  if (isSubmitted) {
    return (
      <div className="bg-[#1a1c1b] min-h-screen pt-24 pb-20 flex items-center justify-center">
        <div className="container mx-auto px-6">
          <div className="max-w-2xl mx-auto text-center bg-[#302f2c] border-2 border-[#d9fb06] p-12 rounded-lg">
            <div className="bg-[#d9fb06] w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-6">
              <Check size={48} className="text-[#1a1c1b]" />
            </div>
            <h2 className="text-[#d9fb06] font-black text-4xl uppercase mb-4">
              Request Received!
            </h2>
            <p className="text-[#dfddd6] text-xl mb-6">
              Thank you for your interest in NexGen MMA Club!
            </p>
            <p className="text-[#888680] text-lg mb-8">
              Our team will contact you within 24 hours to confirm your trial class and answer any questions.
            </p>
            <div className="bg-[#3f4816] p-6 rounded-lg">
              <p className="text-[#dfddd6] mb-2">
                <span className="text-[#d9fb06] font-semibold">Next Steps:</span>
              </p>
              <ul className="text-[#dfddd6] text-left space-y-2 ml-6">
                <li>✓ We'll call you to schedule your free trial</li>
                <li>✓ Bring comfortable workout clothes</li>
                <li>✓ Come ready to train and have fun!</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#1a1c1b] min-h-screen pt-24 pb-20">
      <div className="container mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-12 max-w-4xl mx-auto">
          <h1 className="text-[#d9fb06] font-black text-5xl md:text-7xl uppercase mb-4">
            Book Your Free Trial
          </h1>
          <p className="text-[#dfddd6] text-xl">
            Take the first step towards becoming a fighter. Fill out the form below and we'll get you started.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
          {/* Form */}
          <div className="bg-[#302f2c] border border-[#3f4816] p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Name */}
              <div>
                <label className="text-[#d9fb06] font-semibold uppercase text-sm mb-2 flex items-center">
                  <User size={18} className="mr-2" />
                  Full Name *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full bg-[#1a1c1b] border border-[#3f4816] text-[#dfddd6] px-4 py-3 rounded-lg focus:outline-none focus:border-[#d9fb06] transition-colors"
                  placeholder="Enter your full name"
                  required
                />
              </div>

              {/* Email */}
              <div>
                <label className="text-[#d9fb06] font-semibold uppercase text-sm mb-2 flex items-center">
                  <Mail size={18} className="mr-2" />
                  Email
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full bg-[#1a1c1b] border border-[#3f4816] text-[#dfddd6] px-4 py-3 rounded-lg focus:outline-none focus:border-[#d9fb06] transition-colors"
                  placeholder="your.email@example.com"
                />
              </div>

              {/* Phone */}
              <div>
                <label className="text-[#d9fb06] font-semibold uppercase text-sm mb-2 flex items-center">
                  <Phone size={18} className="mr-2" />
                  Phone Number *
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full bg-[#1a1c1b] border border-[#3f4816] text-[#dfddd6] px-4 py-3 rounded-lg focus:outline-none focus:border-[#d9fb06] transition-colors"
                  placeholder="7011161010"
                  required
                />
              </div>

              {/* Discipline */}
              <div>
                <label className="text-[#d9fb06] font-semibold uppercase text-sm mb-2 block">
                  Preferred Discipline *
                </label>
                <select
                  name="discipline"
                  value={formData.discipline}
                  onChange={handleChange}
                  className="w-full bg-[#1a1c1b] border border-[#3f4816] text-[#dfddd6] px-4 py-3 rounded-lg focus:outline-none focus:border-[#d9fb06] transition-colors"
                  required
                >
                  <option value="">Select a discipline</option>
                  {disciplines.map((discipline) => (
                    <option key={discipline} value={discipline}>
                      {discipline}
                    </option>
                  ))}
                </select>
              </div>

              {/* Preferred Time */}
              <div>
                <label className="text-[#d9fb06] font-semibold uppercase text-sm mb-2 flex items-center">
                  <Calendar size={18} className="mr-2" />
                  Preferred Time *
                </label>
                <select
                  name="preferredTime"
                  value={formData.preferredTime}
                  onChange={handleChange}
                  className="w-full bg-[#1a1c1b] border border-[#3f4816] text-[#dfddd6] px-4 py-3 rounded-lg focus:outline-none focus:border-[#d9fb06] transition-colors"
                  required
                >
                  <option value="">Select preferred time</option>
                  {timeSlots.map((slot) => (
                    <option key={slot} value={slot}>
                      {slot}
                    </option>
                  ))}
                </select>
              </div>

              {/* Message */}
              <div>
                <label className="text-[#d9fb06] font-semibold uppercase text-sm mb-2 block">
                  Additional Message
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows="4"
                  className="w-full bg-[#1a1c1b] border border-[#3f4816] text-[#dfddd6] px-4 py-3 rounded-lg focus:outline-none focus:border-[#d9fb06] transition-colors resize-none"
                  placeholder="Tell us about your goals, experience level, or any questions..."
                ></textarea>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled
                aria-disabled="true"
                title="Temporarily unavailable"
                className="w-full bg-[#d9fb06] text-[#1a1c1b] px-8 py-4 rounded-full font-bold uppercase text-base hover:opacity-90 transition-all hover:scale-105"
              >
                Submit Booking Request (Temporarily Disabled)
              </button>

              <p className="text-[#888680] text-sm text-center">
                * Required fields
              </p>
            </form>
          </div>

          {/* Info Panel */}
          <div className="space-y-6">
            <div className="bg-[#3f4816] p-8 rounded-lg">
              <h3 className="text-[#d9fb06] font-black text-2xl uppercase mb-4">
                What to Expect
              </h3>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <Check size={24} className="text-[#d9fb06] mr-3 flex-shrink-0 mt-1" />
                  <div>
                    <p className="text-[#dfddd6] font-semibold mb-1">Free Trial Class</p>
                    <p className="text-[#888680] text-sm">Experience a full training session at no cost</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <Check size={24} className="text-[#d9fb06] mr-3 flex-shrink-0 mt-1" />
                  <div>
                    <p className="text-[#dfddd6] font-semibold mb-1">Meet Our Coaches</p>
                    <p className="text-[#888680] text-sm">Get introduced to our expert training staff</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <Check size={24} className="text-[#d9fb06] mr-3 flex-shrink-0 mt-1" />
                  <div>
                    <p className="text-[#dfddd6] font-semibold mb-1">Facility Tour</p>
                    <p className="text-[#888680] text-sm">See our world-class training equipment and space</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <Check size={24} className="text-[#d9fb06] mr-3 flex-shrink-0 mt-1" />
                  <div>
                    <p className="text-[#dfddd6] font-semibold mb-1">No Obligation</p>
                    <p className="text-[#888680] text-sm">Try before you commit - no pressure to join</p>
                  </div>
                </li>
              </ul>
            </div>

            <div className="bg-[#302f2c] border border-[#3f4816] p-8">
              <h3 className="text-[#d9fb06] font-black text-2xl uppercase mb-4">
                Contact Us Directly
              </h3>
              <div className="space-y-4">
                <div>
                  <p className="text-[#888680] text-sm uppercase mb-1">Phone</p>
                  <a href="tel:7011161010" className="text-[#dfddd6] text-lg hover:text-[#d9fb06] transition-colors">
                    7011161010
                  </a>
                </div>
                <div>
                  <p className="text-[#888680] text-sm uppercase mb-1">Email</p>
                  <a href="mailto:dojo@nexgenmma.in" className="text-[#dfddd6] text-lg hover:text-[#d9fb06] transition-colors">
                    dojo@nexgenmma.in
                  </a>
                </div>
                <div>
                  <p className="text-[#888680] text-sm uppercase mb-1">WhatsApp</p>
                  <a
                    href="https://wa.me/7011161010"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#dfddd6] text-lg hover:text-[#d9fb06] transition-colors"
                  >
                    Chat with us
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookClass;
